/**
 * InDeep-Net
 * Copyright (C) 2025
 * Authors: Fabien MAREUIL
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import axios from "axios";
import { CleanMessages, JobMessage, WarningMessage } from '../Utils.js';
import { AxiosLoadStructuretoMolstar } from './molstar_structure.js'
import { AxiosLoadTrajectorytoMolstar } from './molstar_trajectory';
import { molstarToindeep, createJob } from './molstar_analysis';
import { loadMRCfile } from './molstar_mrc';
import { ChoicesButton } from './ChoicesButton.jsx';
import { useIsLoaded, useIsActivated, useIsDetected, useIsJobID, updateJobID } from './HookStateUpdate.jsx';
import ChoicesList from './Choices';
import { RangeSlider } from './RangeSlider.jsx';


export const doSubmission = async (plugin, selectedDatabase, structureid) => {
  var url, format;
  if (selectedDatabase === "PDBe") {
    /*url = "https://www.ebi.ac.uk/pdbe/entry-files/download/pdb"*/
    //url = `https://www.ebi.ac.uk/pdbe/static/entry/${structureid.toLowerCase()}_updated.cif`
    url = `https://www.ebi.ac.uk/pdbe/entry-files/download/pdb${structureid.toLowerCase()}.ent`
    //format = 'mmcif'
    format = 'pdb'
  } else if (selectedDatabase === "RCSB") {
    /* https://files.rcsb.org/download/1YCR.cif */
    //url = `https://files.rcsb.org/download/${structureid.toUpperCase()}.cif`
    url = `https://files.rcsb.org/download/${structureid.toUpperCase()}.pdb`
    //format = 'mmcif'
    format = 'pdb'
  } else {
    url = `https://www.alphafold.ebi.ac.uk/api/prediction/${structureid.toUpperCase()}`
    try {
      await axios({
        method: "get",
        url: url,
        responseType: 'json',
      }).then(
        async function (response) {
          //url = response.data[0].cifUrl
          url = response.data[0].pdbUrl

        })
    } catch (error) {
      WarningMessage(`Request ${structureid} on ${selectedDatabase}, ${error}`)
    }
    //format = 'mmcif'
    format = 'pdb'
  }
  try {
    await axios({
      method: "get",
      url: url,
      responseType: 'blob',
    }).then(
      async function (response) {
        const blobfile = new File([response.data], url.substring(url.lastIndexOf('/') + 1));
        const formData = new FormData();
        formData.append("file", blobfile);
        formData.append("format", format);
        AxiosLoadStructuretoMolstar(plugin, formData, format, selectedDatabase);
      }
    );
    document.getElementById("analysis").scrollIntoView({behavior: "smooth"});
  } catch (error) {
    WarningMessage(`Request ${structureid} on ${selectedDatabase}, ${error}`)
  }
}

export const ButtonStructureLoad = forwardRef((props, ref) => {
    const [selectedDatabase, setSelectedDatabase] = useState();
    const [isselectedDatabase, setisselectedDatabase] = useState(false);
    const [isStateok, setisStateok] = useState(false);
    const [isUniprotID, setisUniprotID] = useState(false);
    const [ID, setID] = useState();

    const handleDatabaseSelection = (database) => {
      setSelectedDatabase(database);
      setisselectedDatabase(true)
    };
    let smallmessage = "Enter a PDB ID";
    let label = "PDB id";

    if (selectedDatabase === 'Alphafold') {
      smallmessage = "Enter an uniprot accession an uniprot id";
      label = "Uniprot accession or id";
    }

    const checkState = (event) => {
      if (selectedDatabase === 'Alphafold') {
        const re = new RegExp('[OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2}');
        const re_id = new RegExp('[A-Za-z0-9]{2,}_[A-Za-z0-9_]{2,}');
        if (re.test(event.target.value)) {
          setisUniprotID(false)
          setisStateok(true)
          setID(event.target.value)
        }
        else if (re_id.test(event.target.value)) {
          setisStateok(true)
          setisUniprotID(true)
          setID(event.target.value)
        }
        else {
          setisUniprotID(false)
          setisStateok(false)
        }
      }
      else {
        const re = new RegExp('[A-Za-z0-9]{4}');
        
        if (re.test(event.target.value)) {
          setisStateok(true)
          setID(event.target.value)
        }
        else {
          setisStateok(false)
        }
      }
    }

    useEffect(() => {
      if (isselectedDatabase) {
        document.getElementById("databaseselect").classList.remove('hidden');
      }
    }, [isselectedDatabase]);

    const handleSubmission = async (event) => {
      event.preventDefault()
      if (isUniprotID) {
        var url = `https://rest.uniprot.org/uniprotkb/search?query=id:${ID}&fields=accession,id&size=1`
        try {
          axios({
            method: "get",
            url: url,
            responseType: 'json',
          }).then(
            function (response) {
              if (response.data.results.length === 0) {
                WarningMessage(`Request ${ID} doesn't exist`)
              } else {
                const newID = response.data.results[0].primaryAccession
                setID(newID)
                doSubmission(props.plugin, selectedDatabase, newID);
            }
            })
          } catch (error) {
            WarningMessage(`Request ${ID} doesn't exist, ${error}`)
          }
        }
        else {
      doSubmission(props.plugin, selectedDatabase, ID);
    }
    }

    useImperativeHandle(ref, () => ({
      handleSubmission, // Expose this method to the parent
    }));

    useEffect(() => {
      props.setIsInputEnabled(isStateok);
    }, [isStateok, props]);

    return (
      <div className="rounded-md px-4">
        <h3 className="text-md font-semibold whitespace-nowrap dark:text-gray-50">Select a database</h3>
        <div id="database" className="flex md:flex-row flex-col font-roboto max-h-max md:space-x-4 space-y-4 text-sm justify-center">
          <div className="md:w-1/3 w-full mt-4"><ChoicesButton label='PDBe' handle={ handleDatabaseSelection } selected={ selectedDatabase } value='PDBe' description='search with a pdb id code on the PDBe database' src='logo_pdbe.png'/></div>
          <div className="md:w-1/3 w-full"><ChoicesButton label='RCSB' handle={ handleDatabaseSelection } selected={ selectedDatabase } value='RCSB' description='search with a pdb id code on the RCSB database' src='logo_rcsb.png'/></div>  
          <div className="md:w-1/3 w-full"><ChoicesButton label='Alphafold' handle={ handleDatabaseSelection } selected={ selectedDatabase } value='Alphafold' description='search with a uniprot id on the Alphafold database' src='logo_alphafold.png'/></div>
        </div>
      <div id="databaseselect" className="hidden font-roboto my-4 p-4 bg-stone-200 dark:bg-stone-600 rounded-md">
      <small className="text-gray-600 dark:text-gray-50">{ smallmessage }</small>
      
      <div className="relative flex">
          
      <input
            onChange={checkState}
            type="text"
            id='pdbid'
            className="peer h-full w-full rounded-[7px] border border-gray-200 bg-transparent px-3 py-2.5 pr-20 text-sm font-normal text-gray-700 dark:text-gray-50 outline outline-0 transition-all placeholder-shown:border dark:placeholder-shown:border-gray-200 placeholder-shown:border-gray-700 dark:placeholder-shown:border-t-gray-200 placeholder-shown:border-t-gray-700 focus:border-2 dark:focus:border-sky-950 focus:border-sky-950 dark:focus:border-t-transparent focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-gray-50"
            placeholder=" "
            required
          />
          
          <label className="before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none text-[11px] font-normal leading-tight text-gray-400 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-gray-200 before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-gray-200 after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[3.75] peer-placeholder-shown:text-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-sky-950 dark:peer-focus:text-sky-950 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:before:!border-sky-950 dark:peer-focus:before:!border-sky-950 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:!border-sky-950 dark:peer-focus:after:!border-sky-950 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-gray-500">
            { label }
          </label>
      </div>
      </div>
    </div>
    )
  })

export const ButtonStructureLoadLocal = forwardRef((props,ref) => {
    const [selectedFile, setSelectedFile] = useState();
    const [isFilePicked, setIsFilePicked] = useState(false);

    const changeHandler = (event) => {
      setSelectedFile(event.target.files[0]);
      setIsFilePicked(true);
    };

    const handleSubmission = async (event) => {
      event.preventDefault()
      if (isFilePicked) {
        const format = "pdb";
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("format", format);
        AxiosLoadStructuretoMolstar(props.plugin, formData, format);
        document.getElementById("analysis").scrollIntoView({behavior: "smooth"});
      } else {
        WarningMessage('Select a structure file');
      }
    };

    useImperativeHandle(ref, () => ({
      handleSubmission, // Expose this method to the parent
    }));
  
    useEffect(() => {
      props.setIsInputEnabled(isFilePicked);
    }, [isFilePicked, props]);

    return (
      <div className="bg-gray-200 dark:bg-stone-600 rounded-md p-4 my-4">
        <small className="text-gray-700 dark:text-gray-50">Select a protein structure file</small>

          <div className="flex flex-row">  
              <input  className="h-full w-full rounded-[7px] border bg-blue-200 hover:bg-blue-400 py-2 px-4 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700
  hover:file:bg-violet-100" type="file" name="file" onChange={changeHandler} accept=".pdb,.ent,.cif" />
          </div>

      </div>

    )
  })

export const ButtonTrajectoryLoad = forwardRef((props,ref) => {
    const [selectedFilepdb, setSelectedFilepdb] = useState();
    const [isFilePickedpdb, setIsFilePickedpdb] = useState(false);
    const [selectedFiledcd, setSelectedFiledcd] = useState();
    const [isFilePickeddcd, setIsFilePickeddcd] = useState(false);
    const [selectedFormat, setSelectedFormat] = useState(null);
    const tusid = 1

    const changeHandlerpdb = (event) => {
      setSelectedFilepdb(event.target.files[0]);
      setIsFilePickedpdb(true);
    };
    const changeHandlerdcd = (event) => {
      setSelectedFiledcd(event.target.files[0]);
      setIsFilePickeddcd(true);
    };
    const handleChoiceSelected = (choiceValue) => {
      setSelectedFormat(choiceValue);
    };

    const handleSubmission = async (event) => {
      event.preventDefault()
      if (isFilePickedpdb && isFilePickeddcd && selectedFormat) {
        const formatstructure = "pdb";
        const formattrajectory = selectedFormat;
        const formDataStructure = new FormData();
        const formDataTrajectory = new FormData();
        formDataStructure.append("file", selectedFilepdb);
        formDataStructure.append("format", formatstructure);
        formDataTrajectory.append("file", selectedFiledcd);
        formDataTrajectory.append("format", formattrajectory);
        AxiosLoadTrajectorytoMolstar(props.plugin, formDataStructure, formDataTrajectory, formatstructure, formattrajectory, tusid)
        document.getElementById("volumediv").classList.remove('hidden')
        document.getElementById("analysis").scrollIntoView({behavior: "smooth"});
      } else if (!isFilePickedpdb) {
        WarningMessage('Select a structure file');
      } else if (!isFilePickeddcd) {
        WarningMessage('Select a trajectory file');
      } else {
        WarningMessage('Select a format');
      }
    };
    const checkFormatSelected = () => {
      if (selectedFormat) {
        document.getElementById("filedcd").removeAttribute('disabled');
      }
    }

    useEffect(() => {checkFormatSelected()}, [selectedFormat]);

    useImperativeHandle(ref, () => ({
      handleSubmission, // Expose this method to the parent
    }));

    useEffect(() => {
      props.setIsInputEnabled(isFilePickedpdb && isFilePickeddcd);
    }, [isFilePickedpdb, isFilePickeddcd, props]);

    


    return (
      
      <div className="bg-gray-200 dark:bg-stone-600 rounded-md p-4 my-4">
      
        <div className="flex flex-row space-x-2">
          <div className="w-full">
            <small className="text-gray-700 dark:text-gray-50">Select a protein structure file</small>
            <input id="filepdbid" className="w-full rounded-[7px] bg-blue-200 hover:bg-blue-400 py-2 px-4 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100" type="file" name="filepdb" onChange={changeHandlerpdb} accept=".pdb,.ent" />
          </div>
        </div>
        <div className="flex">
          <div className="w-1/4 py-2">
            <small className="text-gray-700 dark:text-gray-50">Select a trajectory format</small>
            <ChoicesList url="/api/trajectory" field="format" onChoiceSelected={handleChoiceSelected}/>
          </div> 
          <div className="w-3/4 py-2">  
            <small className="text-gray-700 dark:text-gray-50">Select a trajectory file</small>
            <input disabled id="filedcd" className="w-full rounded-[7px] bg-blue-200 hover:bg-blue-400 py-2 px-4 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100 file:disabled:text-gray-700 file:disabled:bg-gray-200 hover:file:disabled:bg-gray-200 disabled:bg-gray-400 disabled:hover:bg-gray-400" type="file" name="filedcd" onChange={changeHandlerdcd} accept="*" />
          </div>
        </div>
        
        
        
    
    </div>
    )
  })

export const ButtonMRCLoad = forwardRef((props, ref) => {
    const [selectedFile, setSelectedFile] = useState();
    const [isFilePicked, setIsFilePicked] = useState(false);

    const changeHandler = (event) => {
      setSelectedFile(event.target.files[0]);
      setIsFilePicked(true);
    };

    const handleSubmission = () => {
      if (isFilePicked) {
        loadMRCfile(props.plugin, selectedFile);
        document.getElementById("analysis").scrollIntoView({behavior: "smooth"});
      } else {
        WarningMessage('Select a mrc file');
      }
    };

    useImperativeHandle(ref, () => ({
      handleSubmission, // Expose this method to the parent
    }));
  
    useEffect(() => {
      props.setIsInputEnabled(isFilePicked);
    }, [isFilePicked, props]);

    return (
      <div className="bg-gray-200 dark:bg-stone-600 rounded-md p-4 my-4">
        <small className="text-gray-700 dark:text-gray-50">Select a MRC file</small>
        <div id="fileid" className="flex flex-row">
          <input className="h-full w-full rounded-[7px] border bg-blue-200 hover:bg-blue-400 py-2 px-4 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700
  hover:file:bg-violet-100" type="file" name="file" onChange={changeHandler} accept=".mrc" />
        </div>
      </div>
    )
  })

export function ButtonClear(props) {
    
    const isLoaded = useIsLoaded();

    let handleButtonClick = () => {
      sessionStorage.setItem('structures', JSON.stringify({}));
      let volumediv = document.getElementById('volumediv');
      volumediv.classList.add('hidden');
      props.plugin.clear()
      const hrefexport = document.getElementById('exporthref');
      hrefexport.href = "#";
      if (!hrefexport.classList.contains('disabled')) {
        hrefexport.classList.add('disabled')
      };
      updateJobID('');
      CleanMessages();

      document.getElementById("analysis").scrollIntoView({behavior: "smooth"});
    }
    return (
      <button id="tourclear" className="select-none rounded dark:bg-blue-300 hover:bg-red-700 bg-blue-300 dark:hover:bg-red-700 py-2 px-4 text-center align-middle text-xs font-bold uppercase text-white shadow-md dark:shadow-blue-600/20 shadow-blue-800/20 transition-all hover:shadow-lg dark:hover:shadow-red-600/40 hover:shadow-red-800/40 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none peer-placeholder-shown:pointer-events-none peer-placeholder-shown:bg-gray-500 peer-placeholder-shown:opacity-50 peer-placeholder-shown:shadow-none disabled:opacity-50 disabled:bg-blue-300 disabled:dark:bg-blue-300 disabled:dark:hover:bg-blue-300 disabled:shadow-none disabled:hover:bg-blue-300" onClick={handleButtonClick} disabled={!isLoaded}>Clear Data</button>
      )
  }

export function ButtonAnalysis(props) {
    const isLoaded = useIsLoaded();
    const isActivated = useIsActivated();
    const isDetected = useIsDetected();
    const [volumeValue, setVolumeValue] = useState(0);
    const taskIdstocked = useIsJobID();
    const handleVolumeChange = (value) => {
      setVolumeValue(value);
    };

    let handleButtonClick = async (event) => {
      event.preventDefault()
      const useholo = false;
      const email = document.getElementById('email').value ? document.getElementById('email').value : "empty";
      if (email === 'empty') {
        WarningMessage("No email provided, don't lose the URL above.");
      }
      var indeep_data = molstarToindeep(props.plugin);
      createJob(props.plugin, indeep_data['namestructure'], indeep_data['pymolselect'], volumeValue, email, useholo);
    }

    let handleButtonHoloClick = async (event) => {
      event.preventDefault()
      const useholo = true;
      const email = document.getElementById('email').value ? document.getElementById('email').value : "empty";
      if (email === 'empty') {
        WarningMessage("No email provided, don't lose the URL above.");
      };
      var indeep_data = molstarToindeep(props.plugin);
        
      if (!indeep_data['pymolselect']) {
        JobMessage("Select residues for perform an InDeepHolo calculation", "yellow");
      } else {
        createJob(props.plugin, indeep_data['namestructure'], indeep_data['pymolselect'], volumeValue, email, useholo, taskIdstocked);
      };
    }

    return (
     
     <div className="flex flex-col font-roboto">  
        <RangeSlider id='volumediv' min={50} max={300} onValueChange={handleVolumeChange} />
        <div className="flex justify-end space-x-2"> 
          <button
              id="tourdetectbutton"
              className="mb-2 select-none rounded bg-amber-500 hover:bg-amber-600 disabled:hover:bg-amber-500 py-2 px-4 text-xs font-bold uppercase text-white shadow-md dark:shadow-orange-600/20 shadow-orange-800/20 transition-all hover:shadow-lg dark:hover:shadow-orange-600/40 hover:shadow-orange-800/40 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none peer-placeholder-shown:pointer-events-none peer-placeholder-shown:bg-gray-500 peer-placeholder-shown:opacity-50 peer-placeholder-shown:shadow-none disabled:opacity-50 disabled:shadow-none"
              type="button"
              data-ripple-light="true"
              onClick={handleButtonClick}
              disabled={!(isLoaded && isActivated)}
            >
              {props.label}
          </button>
          {isDetected && (
            <button
            id="tourholobutton"
            className="mb-2 select-none rounded bg-amber-500 hover:bg-amber-600 disabled:hover:bg-amber-500 py-2 px-4 text-xs font-bold uppercase text-white shadow-md dark:shadow-orange-600/20 shadow-orange-800/20 transition-all hover:shadow-lg dark:hover:shadow-orange-600/40 hover:shadow-orange-800/40 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none peer-placeholder-shown:pointer-events-none peer-placeholder-shown:bg-gray-500 peer-placeholder-shown:opacity-50 peer-placeholder-shown:shadow-none disabled:opacity-50 disabled:shadow-none"
            type="button"
            data-ripple-light="true"
            onClick={handleButtonHoloClick}
          >
            use InDeep<sup>holo</sup>
        </button>         )}
      </div> 
    </div>)
}